import React from 'react'
import { LanguageTag } from '@/components/features/spaces/LanguageTag'
import { ROUTES } from '@/lib/routes'
import { isSafeExternalUrl } from '@/lib/safeUrl'
import { iconComponent } from '@/components/ui/icon-picker'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useRoute } from '@/lib/RouteContext'
import { useSidebar } from '@/lib/SidebarContext'
import { useMenu } from '@/lib/MenuContext'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { moduleIcon } from '@/modules/registry'
import { ChannelUnreadSync } from '@/components/features/channel/ChannelUnreadSync'
import { useChannelUnread } from '@/components/features/channel/channelUnread'
import { MenuCountersSync } from './MenuCountersSync'
import { useMenuCounters } from '@/lib/menuCounters'
import { NewsBadge } from './menuBadges'
import { SidebarPersonalLinks } from './PersonalLinks'
import {
  ChevronDown,
  ChevronRight,
  Home,
  Users,
  ExternalLink,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  GripVertical,
  Settings,
  DollarSign,
  Mail,
  Workflow,
  Percent,
  Building2,
  User,
  Lock,
  LayoutDashboard,
  RefreshCw,
  History,
  Activity,
  BarChart3,
  Globe,
  Library
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
  useDroppable,
  type CollisionDetection,
  defaultDropAnimationSideEffects,
  type DropAnimation,
  type Modifier,
  type DraggableAttributes,
  type DraggableSyntheticListeners
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type Section } from '@/types/sections'
import { type Space } from '@/types/spaces'
import { sdk } from '@/lib/sdk'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CreateSectionDialog } from '@/components/features/sections/CreateSectionDialog'
import { EditSectionDialog } from '@/components/features/sections/EditSectionDialog'
import { DeleteSectionDialog } from '@/components/features/sections/DeleteSectionDialog'
import { MONETIZATION_ROLES, CAMPAIGN_ROLES, TEAM_ROLES, LOGS_ROLES, ENGAGEMENT_ROLES, ANALYTICS_ROLES } from '@/lib/permissions'
import { MEMBERS_AREA_ROLES } from '@/components/features/members/membersTabs'
import { CreateSpaceDialog } from '@/components/features/spaces/CreateSpaceDialog'
import { EditSpaceDialog } from '@/components/features/spaces/EditSpaceDialog'
import { DeleteSpaceDialog } from '@/components/features/spaces/DeleteSpaceDialog'
import { AppLink } from '@/components/ui/app-link'
import { AdminOnlyNotice } from '@/components/features/spaces/AdminOnlyNotice'
import { lockReasonKey } from '@/types/access'
import { useHasCookieChoice } from '@/components/features/cookies/useHasCookieChoice'
import { openCookieNotice } from '@/lib/cookieConsent'

export interface AdminMenuItem { path: string; icon: LucideIcon; label: string; visible: boolean }

/**
 * The Administration menu, in order, shared by the full menu and the rail.
 * Each page's RoleGate and the API agree with `visible`: Monetization is
 * owner/admin/finance (finance reads, F-32), Campaigns the whole team (F-73),
 * Logs owner/admin and finance (financial only), Settings owner/admin, the last
 * two SuperAdmin only (F-43, F-65). A new admin page is one line here.
 */
export const useAdminItems = (): AdminMenuItem[] => {
  const { t } = useTranslation(['navigation', 'common'])
  const { hasRole, user } = useAuth()
  return [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), visible: hasRole(TEAM_ROLES) },
    { path: '/members', icon: Users, label: t('members'), visible: hasRole(MEMBERS_AREA_ROLES) },
    { path: '/monetization', icon: DollarSign, label: t('monetization'), visible: hasRole(MONETIZATION_ROLES) },
    { path: '/engagement', icon: Activity, label: t('engagement'), visible: hasRole(ENGAGEMENT_ROLES) },
    { path: '/analytics', icon: BarChart3, label: t('analytics'), visible: hasRole(ANALYTICS_ROLES) },
    { path: '/campaigns', icon: Mail, label: t('campaigns'), visible: hasRole(CAMPAIGN_ROLES) },
    /* F-141: owner and admin edit, moderator and finance read (the API's roles). */
    { path: '/workflows', icon: Workflow, label: t('workflows'), visible: hasRole(TEAM_ROLES) },
    /* F-178 1b: the community's audios and videos, reused anywhere (the team; owner and admin delete). */
    { path: '/media-library', icon: Library, label: t('mediaLibrary'), visible: hasRole(TEAM_ROLES) },
    { path: '/logs', icon: History, label: t('logs'), visible: hasRole(LOGS_ROLES) },
    { path: '/settings', icon: Settings, label: t('settings'), visible: hasRole(['owner', 'admin']) },
    { path: ROUTES.adminFees, icon: Percent, label: t('platformFees'), visible: Boolean(user?.isSuperAdmin) },
    { path: ROUTES.adminCommunities, icon: Building2, label: t('adminCommunities'), visible: Boolean(user?.isSuperAdmin) },
    { path: ROUTES.adminPlatform, icon: Globe, label: t('entitlements:platform.menu'), visible: Boolean(user?.isSuperAdmin) },
  ]
}

// Adjust overlay vertical position to account for fixed header (h-16 = 64px)
const HEADER_HEIGHT = 64
const adjustTopModifier: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - HEADER_HEIGHT,
  }
}

/* The picker's own resolver, so a name the picker maps (APP-70: `tool`)
   shows the same icon here as in the picker. */
export const getIconComponent = (iconName: string) => (iconName ? iconComponent(iconName) : Home)

export const isRouteActive = (currentPath: string, itemPath: string) => {
  if (itemPath === '/') {
    return currentPath === '/'
  }
  return currentPath.startsWith(itemPath)
}

// Space content component (reusable for both sortable item and drag overlay)
interface SpaceContentProps {
  space: Space
  section: Section
  currentPath: string
  defaultHomeSpace: Space | null
  canEdit: boolean
  isActive: boolean
  isDragOverlay?: boolean
  onEditSpace?: (space: Space) => void
  onDeleteSpace?: (space: Space) => void
  dragHandleProps?: { attributes: DraggableAttributes; listeners: DraggableSyntheticListeners }
}

const SpaceContent: React.FC<SpaceContentProps> = ({
  space,
  section,
  canEdit,
  isActive,
  isDragOverlay = false,
  onEditSpace,
  onDeleteSpace,
  dragHandleProps
}) => {
  const { t } = useTranslation(['navigation', 'common'])
  const Icon = getIconComponent(space.icon) || moduleIcon(space.module)
  /* F-138: a channel with someone else's new message reads bold, with the unread mentions beside it. */
  const channelUnread = useChannelUnread(space.module === 'channel' ? space.id : undefined)
  const channelBold = Boolean(channelUnread?.hasUnread || channelUnread?.mentions) && !isDragOverlay
  const channelMentions = channelUnread?.mentions ?? 0
  /* F-191 phase 2b: new posts or events since the last visit (Feed, Content, Events). */
  const news = useMenuCounters().spaces[space.id] ?? 0

  /* The server sent it and marked it closed, which means it is closed by
     something that can be bought. Drawing the padlock is the whole point:
     a space nobody can see is a space nobody buys. */
  const lockKey = space.locked ? lockReasonKey(space.lockReason) ?? 'common:access.lockReason.product' : null
  const lockLabel = lockKey ? t(lockKey) : null

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors group",
        isDragOverlay
          ? "bg-white shadow-xl border border-gray-200 cursor-grabbing"
          : isActive
            ? "bg-primary-50 backdrop-blur-sm text-primary shadow-[0_4px_12px_rgba(255,101,0,0.12)]"
            : "text-gray-600 hover:bg-gray-50"
      )}
    >
      {(() => {
        const label = (
          <>
            <Icon className={cn("h-4 w-4 flex-shrink-0", isDragOverlay && "text-gray-600")} />
            <span className={cn("truncate", isDragOverlay && "text-gray-700", channelBold && "font-semibold text-gray-900")} data-channel-unread={channelBold ? 'true' : undefined}>{space.title}</span>
            {channelMentions > 0 && !isDragOverlay && (
              <span
                className="ml-auto inline-flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white"
                aria-label={t('common:channelMenu.mentions', { count: channelMentions })}
                data-channel-mentions={channelMentions}
              >
                {channelMentions > 99 ? '99+' : channelMentions}
              </span>
            )}
            {news > 0 && channelMentions === 0 && !isDragOverlay && !isActive && (
              <NewsBadge count={news} className="ml-auto" />
            )}
            {space.module === 'url' && !space.locked && (
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            )}
            {!isDragOverlay && <LanguageTag languages={space.languages} />}
            {space.adminOnly && !isDragOverlay && <AdminOnlyNotice variant="badge" />}
            {lockLabel && (
              <span title={lockLabel} aria-label={lockLabel} className="flex-shrink-0">
                <Lock className="h-3 w-3 text-gray-400" />
              </span>
            )}
          </>
        )
        const itemClass = "flex items-center gap-2 flex-1 text-left min-w-0"
        if (isDragOverlay) return <span className={itemClass}>{label}</span>
        /* A real link, so Cmd/Ctrl-click and the context menu open the space in
           a new tab. A URL space opens its external address in a new tab;
           SEC-40: only http(s), any other scheme (javascript:, data:) is inert. */
        /* F-161a: a padlocked Link space opens the offer, never the link (its address does not even come). */
        if (space.module === 'url' && !space.locked) {
          const external = space.externalUrl && isSafeExternalUrl(space.externalUrl) ? String(space.externalUrl).trim() : null
          return external
            ? <a href={external} target="_blank" rel="noopener noreferrer" className={cn(itemClass, "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary")}>{label}</a>
            : <span className={itemClass}>{label}</span>
        }
        return (
          <AppLink to={`/${section.slug}/${space.slug}`} aria-current={isActive ? 'page' : undefined} className={itemClass}>
            {label}
          </AppLink>
        )
      })()}
      {canEdit && !isDragOverlay && (
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded flex-shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                aria-label={t('sidebar.spaceOptions')}
              >
                <MoreHorizontal className="h-4 w-4 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right" className="w-48">
              <DropdownMenuItem onClick={() => onEditSpace?.(space)}>
                <Pencil className="h-4 w-4 mr-2" />
                {t('sidebar.editSpace')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDeleteSpace?.(space)} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                {t('sidebar.deleteSpace')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div
            className="p-1 rounded flex-shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
            aria-label={t('sidebar.reorderSpace')}
            {...dragHandleProps?.attributes}
            {...dragHandleProps?.listeners}
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      )}
    </div>
  )
}

interface SortableSpaceItemProps {
  space: Space
  section: Section
  sections: Section[]
  canEdit: boolean
  currentPath: string
  defaultHomeSpace: Space | null
  onEditSpace: (space: Space) => void
  onDeleteSpace: (space: Space) => void
}

const SortableSpaceItem: React.FC<SortableSpaceItemProps> = ({
  space,
  section,
  canEdit,
  currentPath,
  defaultHomeSpace,
  onEditSpace,
  onDeleteSpace
}) => {
  const { params } = useRoute()
  /* `data` is what tells the drag handlers which list this item started in.
     Without it, a space dropped on another section has no origin to reorder. */
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: space.id, data: { type: 'space', sectionId: section.id } })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1
  }

  const spacePath = `/${section.slug}/${space.slug}`

  // Check if this space is active by matching path OR params
  const isActiveByPath = currentPath === spacePath || (currentPath === '/' && defaultHomeSpace?.id === space.id)
  const isActiveByParams = params.sectionSlug === section.slug && params.spaceSlug === space.slug
  const isActive = space.module === 'url' && !space.locked ? false : (isActiveByPath || isActiveByParams)

  return (
    <div ref={setNodeRef} style={style}>
      <SpaceContent
        space={space}
        section={section}
        currentPath={currentPath}
        defaultHomeSpace={defaultHomeSpace}
        canEdit={canEdit}
        isActive={isActive}
        onEditSpace={onEditSpace}
        onDeleteSpace={onDeleteSpace}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  )
}

interface SidebarSectionProps {
  section: Section
  isAuthenticated: boolean
  currentPath: string
  sections: Section[]
  canEdit: boolean
  onRefresh: () => void
}

const SidebarSection = ({ section, currentPath, sections, canEdit, onRefresh }: SidebarSectionProps) => {
  const { t } = useTranslation('navigation')
  const [isExpanded, setIsExpanded] = React.useState(true)
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [editSpaceDialogOpen, setEditSpaceDialogOpen] = React.useState(false)
  const [deleteSpaceDialogOpen, setDeleteSpaceDialogOpen] = React.useState(false)
  const [selectedSpace, setSelectedSpace] = React.useState<Space | null>(null)

  /* A route into this section (a link in a post, Back, a notification) opens
     it, so the space it highlights is not hidden in a collapsed section. */
  const routeSectionSlug = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)[0]
  React.useEffect(() => {
    if (routeSectionSlug === section.slug) setIsExpanded(true)
  }, [routeSectionSlug, section.slug])

  // Get visible spaces count for this section
  /* The spaces come straight from the menu state, which is where a drag now
     writes them. A local copy here would have to be merged back on every
     cross-section move, and would show a stale list for the duration. */
  const visibleSpaces = React.useMemo(() => {
    const spaces = section.spaces || []
    if (!spaces.length) return []

    /* The API already decided. It knows which groups this person is in and
       what they bought — this component does not, and the list it used to
       filter by hand hid `plan` from everybody including admins, and would hide
       `private` the same silent way.

       What the server sends is what may be drawn. A space it chose to send with
       `locked` is drawn with a padlock instead of being hidden: it is closed by
       something that can be bought, and hiding the offer hides the sale. */
    return spaces
  }, [section.spaces])

  /* The space the home draws on `/`: the first one this person can open (F-161a), wherever it is. */
  const { getHomeSpace } = useMenu()
  const defaultHomeSpace = React.useMemo(() => {
    if (currentPath !== '/') return null
    const home = getHomeSpace()
    return home && home.sectionId === section.id ? home : null
  }, [currentPath, getHomeSpace, section.id])
  
  const visibleSpacesCount = visibleSpaces.length
  const hasSpaces = visibleSpacesCount > 0
  /* F-161a: padlocked as one line (no spaces come): the row opens the section page. */
  const lockedLine = Boolean(section.locked) && !hasSpaces
  const sectionLockKey = section.locked ? lockReasonKey(section.lockReason) : null
  const sectionPath = `/${section.slug}`
  const isSectionActive = currentPath.split(/[?#]/)[0] === sectionPath
  
  const handleEditSection = () => {
    setEditDialogOpen(true)
  }

  const handleDeleteSection = () => {
    setDeleteDialogOpen(true)
  }

  const handleEditSpace = (space: Space) => {
    setSelectedSpace(space)
    setEditSpaceDialogOpen(true)
  }

  const handleDeleteSpace = (space: Space) => {
    setSelectedSpace(space)
    setDeleteSpaceDialogOpen(true)
  }

  /* A section is both a sortable item and a place to drop a space into. The
     droppable is what makes an empty or collapsed section a valid target: with
     only the spaces sortable, a section with nothing in it could never receive
     anything, which is exactly when you most want to move something there. */
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `section-drop-${section.id}`,
    data: { type: 'section-container', sectionId: section.id }
  })

  const SectionIcon = getIconComponent(section.icon || 'folder')
  const sectionNews = useMenuCounters().sections[section.id] ?? 0

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({ id: section.id, data: { type: 'section' } })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.3 : 1
  }

  return (
    <div ref={setNodeRef} style={style} className="space-y-1">
      <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg hover:bg-gray-50 group">
        {(() => {
          const label = (
            <>
              <SectionIcon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{section.title}</span>
              <LanguageTag languages={section.languages} />
              {section.adminOnly && <AdminOnlyNotice variant="badge" />}
              {/* F-191 phase 2b: the section's sum, while its spaces are folded away. */}
              {!isExpanded && sectionNews > 0 && <NewsBadge count={sectionNews} className="ml-auto" />}
              {sectionLockKey && (
                <span title={t(sectionLockKey)} aria-label={t(sectionLockKey)} className="flex-shrink-0" data-section-locked>
                  <Lock className="h-3 w-3 text-gray-400" />
                </span>
              )}
            </>
          )
          return lockedLine ? (
            <AppLink
              to={sectionPath}
              aria-current={isSectionActive ? 'page' : undefined}
              className={cn('flex items-center gap-2 flex-1 text-sm font-medium text-left min-w-0', isSectionActive ? 'text-primary' : 'text-gray-700')}
            >
              {label}
            </AppLink>
          ) : (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 flex-1 text-sm font-medium text-gray-700 text-left min-w-0"
            >
              {label}
            </button>
          )
        })()}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {canEdit && (
            <>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="p-1 rounded flex-shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                  aria-label={t('sidebar.sectionOptions')}
                >
                  <MoreHorizontal className="h-4 w-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="right" className="w-48">
                <DropdownMenuItem onClick={handleEditSection}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('sidebar.editSection')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeleteSection} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('sidebar.deleteSection')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
              <div
                className="p-1 rounded flex-shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                aria-label={t('sidebar.reorderSection')}
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </div>
            </>
          )}
          {hasSpaces && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </button>
          )}
        </div>
      </div>
      
      {isExpanded && hasSpaces && (
        <SortableContext
          items={visibleSpaces.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div
            ref={setDropRef}
            className={cn(
              'ml-4 space-y-1 rounded-lg transition-colors',
              isOver && 'bg-primary-50/60'
            )}
          >
            {visibleSpaces.map(space => (
              <SortableSpaceItem
                key={space.id}
                space={space}
                section={section}
                sections={sections}
                canEdit={canEdit}
                currentPath={currentPath}
                defaultHomeSpace={defaultHomeSpace}
                onEditSpace={handleEditSpace}
                onDeleteSpace={handleDeleteSpace}
              />
            ))}
          </div>
        </SortableContext>
      )}
      {isExpanded && !hasSpaces && !lockedLine && (
        <div
          ref={setDropRef}
          className={cn(
            'ml-4 space-y-1 rounded-lg transition-colors',
            isOver && 'bg-primary-50/60'
          )}
        >
          <div className="px-3 py-2 text-xs text-gray-500">
            {t('sidebar.noSpacesAvailable')}
          </div>
        </div>
      )}

      {canEdit && isExpanded && (
        <div className="ml-4 space-y-1">
          <CreateSpaceDialog section={section} onSpaceCreated={onRefresh} trigger="button" />
        </div>
      )}

      <EditSectionDialog
        section={section}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSectionUpdated={onRefresh}
      />

      <DeleteSectionDialog
        section={section}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSectionDeleted={onRefresh}
      />

      {selectedSpace && (
        <>
          <EditSpaceDialog
            space={selectedSpace}
            open={editSpaceDialogOpen}
            onOpenChange={setEditSpaceDialogOpen}
            onSpaceUpdated={onRefresh}
          />

          <DeleteSpaceDialog
            space={selectedSpace}
            open={deleteSpaceDialogOpen}
            onOpenChange={setDeleteSpaceDialogOpen}
            onSpaceDeleted={onRefresh}
          />
        </>
      )}
    </div>
  )
}

/**
 * Which droppable the pointer is actually over.
 *
 * `closestCenter` alone put a space in the wrong section: it compares centers,
 * and a section's whole list of spaces is a droppable too, so the center of a
 * tall list three sections down could sit closer than the row right below the
 * cursor. Dragging a space one position within its own section would send it
 * somewhere else entirely.
 *
 * So: only droppables that can receive what is being dragged, the pointer over
 * everything else, and a row preferred over the list that contains it — the
 * pointer is inside both at once, and the row is the more precise answer.
 */
const sidebarCollisionDetection: CollisionDetection = (args) => {
  const activeType = args.active.data.current?.type

  const droppableContainers = args.droppableContainers.filter((container) => {
    const type = container.data.current?.type
    return activeType === 'space'
      ? type === 'space' || type === 'section-container'
      : type === 'section'
  })

  const overPointer = pointerWithin({ ...args, droppableContainers })

  if (overPointer.length > 0) {
    const overSpace = overPointer.find(
      (collision) =>
        droppableContainers.find((container) => container.id === collision.id)?.data.current?.type === 'space'
    )
    return overSpace ? [overSpace] : overPointer
  }

  /* Outside every droppable — between two sections, say. Falling back keeps the
     drag alive instead of dropping the item back where it started. */
  return closestCenter({ ...args, droppableContainers })
}

/** Which section currently holds a space, by id. */
const findSectionIdOfSpace = (spaceId: string, sections: Section[]): string | undefined =>
  sections.find(section => (section.spaces || []).some(space => space.id === spaceId))?.id

/**
 * Moves a space to another section, at a given position, and renumbers both
 * lists. Pure: it returns a new sections array, so it can be used both for the
 * optimistic update during the drag and for the payload sent afterwards.
 */
const moveSpaceBetweenSections = (
  sections: Section[],
  spaceId: string,
  toSectionId: string,
  toIndex: number
): Section[] => {
  const fromSectionId = findSectionIdOfSpace(spaceId, sections)
  if (!fromSectionId) return sections

  const moving = (sections.find(s => s.id === fromSectionId)?.spaces || []).find(sp => sp.id === spaceId)
  if (!moving) return sections

  return sections.map(section => {
    if (section.id === fromSectionId && section.id === toSectionId) {
      const current = section.spaces || []
      const oldIndex = current.findIndex(sp => sp.id === spaceId)
      const reordered = arrayMove(current, oldIndex, toIndex)
      return { ...section, spaces: reordered.map((sp, index) => ({ ...sp, order: index })) }
    }

    if (section.id === fromSectionId) {
      const kept = (section.spaces || []).filter(sp => sp.id !== spaceId)
      return { ...section, spaces: kept.map((sp, index) => ({ ...sp, order: index })) }
    }

    if (section.id === toSectionId) {
      const current = [...(section.spaces || [])]
      const at = toIndex < 0 || toIndex > current.length ? current.length : toIndex
      current.splice(at, 0, { ...moving, sectionId: toSectionId })
      return { ...section, spaces: current.map((sp, index) => ({ ...sp, order: index })) }
    }

    return section
  })
}

export const Sidebar = () => {
  const { t } = useTranslation(['navigation', 'common'])
  const { isOpen, close, isCollapsed, mode, isTopNav } = useSidebar()
  const { currentPath } = useRoute()
  const { sections, isLoading, error, refresh, updateSections, holdLive } = useMenu()
  /* F-138: the channels in the menu, whose unread state the menu shows. */
  const channelSpaceIds = React.useMemo(
    () => sections.flatMap((section) => (section.spaces ?? []).filter((space) => space.module === 'channel').map((space) => space.id)),
    [sections]
  )
  const { isAuthenticated, hasRole } = useAuth()
  const { community } = useCommunity()
  const hasCookieChoice = useHasCookieChoice()
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)
  const [activeId, setActiveId] = React.useState<string | null>(null)
  const [activeType, setActiveType] = React.useState<'section' | 'space' | null>(null)
  /* Where the space was when the drag began. `sections` is rewritten while the
     pointer moves, so by the time it is dropped the old section is gone from
     state — and it is the one that also has to be renumbered on the server. */
  const dragOriginSectionId = React.useRef<string | null>(null)

  // Check if user can edit (owner or admin)
  const canEdit = hasRole(['owner', 'admin'])

  const adminItems = useAdminItems()
  const canSeeAdmin = adminItems.some((item) => item.visible)

  /* Administration accordion: closed by default below `lg` (drawer mode), open on
     desktop. The saved choice is per device; an active admin route forces it open. */
  const adminRouteActive = adminItems.some((item) => isRouteActive(currentPath, item.path))
  const [adminOpen, setAdminOpen] = React.useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('sidebar.adminOpen')
      if (saved === 'true' || saved === 'false') return saved === 'true'
    } catch { /* storage unavailable */ }
    return typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  })
  React.useEffect(() => {
    if (adminRouteActive) setAdminOpen(true)
  }, [adminRouteActive])
  const toggleAdmin = () => {
    const next = !adminOpen
    setAdminOpen(next)
    try { localStorage.setItem('sidebar.adminOpen', String(next)) } catch { /* ignore */ }
  }

  const handleAddSection = () => {
    setCreateDialogOpen(true)
  }

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const type = event.active.data.current?.type === 'space' ? 'space' : 'section'
    /* A live menu update mid-drag would pull the list from under the pointer. */
    holdLive(true)
    setActiveId(event.active.id as string)
    setActiveType(type)
    dragOriginSectionId.current =
      type === 'space' ? findSectionIdOfSpace(event.active.id as string, sections) ?? null : null
  }

  /**
   * Crossing into another section, while the pointer is still down.
   *
   * dnd-kit has no notion of "the list changed" on drop alone: the item has to
   * already be in the destination for the drop position to mean anything. So
   * the move happens here, against the menu state, and the drop only decides
   * the final position and persists it.
   */
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over || active.data.current?.type !== 'space') return

    const fromSectionId = findSectionIdOfSpace(active.id as string, sections)
    const overType = over.data.current?.type
    const toSectionId =
      overType === 'space'
        ? findSectionIdOfSpace(over.id as string, sections)
        : (over.data.current?.sectionId as string | undefined)

    if (!fromSectionId || !toSectionId || fromSectionId === toSectionId) return

    const destination = sections.find(s => s.id === toSectionId)?.spaces || []
    const overIndex = overType === 'space' ? destination.findIndex(sp => sp.id === over.id) : destination.length

    updateSections(moveSpaceBetweenSections(sections, active.id as string, toSectionId, overIndex))
  }

  /** Reorder inside one section, or land a space that came from another one. */
  const handleSpaceDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    const spaceId = active.id as string
    const fromSectionId = dragOriginSectionId.current
    const currentSectionId = findSectionIdOfSpace(spaceId, sections)

    if (!currentSectionId) return

    const overType = over?.data.current?.type
    const targetSectionId =
      !over
        ? currentSectionId
        : overType === 'space'
          ? findSectionIdOfSpace(over.id as string, sections) ?? currentSectionId
          : (over.data.current?.sectionId as string | undefined) ?? currentSectionId

    const destination = sections.find(s => s.id === targetSectionId)?.spaces || []
    const overIndex =
      overType === 'space' ? destination.findIndex(sp => sp.id === over!.id) : destination.length - 1

    const next = moveSpaceBetweenSections(sections, spaceId, targetSectionId, overIndex)
    updateSections(next)

    const movedSection = fromSectionId && fromSectionId !== targetSectionId
    const payloadFor = (sectionId: string) =>
      (next.find(s => s.id === sectionId)?.spaces || []).map((sp, index) => ({ id: sp.id, order: index }))

    try {
      if (movedSection) {
        const moved = (next.find(s => s.id === targetSectionId)?.spaces || []).find(sp => sp.id === spaceId)
        /* The slug travels with it: two sections can each hold a "/eventos",
           and the API only dedupes a slug it was asked to write. */
        await sdk.spaces.update(spaceId, {
          sectionId: targetSectionId,
          slug: moved?.slug,
          order: moved?.order ?? 0
        })
        await sdk.spaces.reorderSpaces(targetSectionId, payloadFor(targetSectionId))
        if (fromSectionId) await sdk.spaces.reorderSpaces(fromSectionId, payloadFor(fromSectionId))
        /* The server may have had to change the slug to keep it unique in the
           destination, and the route to this space is built from it. */
        await refresh()
      } else {
        await sdk.spaces.reorderSpaces(targetSectionId, payloadFor(targetSectionId))
      }
    } catch (error) {
      console.error('❌ Failed to move space:', error)
      await refresh()
    }
  }

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (active.data.current?.type === 'space') {
      await handleSpaceDragEnd(event)
      setActiveId(null)
      setActiveType(null)
      dragOriginSectionId.current = null
      holdLive(false)
      return
    }

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id)
      const newIndex = sections.findIndex((s) => s.id === over.id)

      // Reorder the sections array
      const newSections = arrayMove(sections, oldIndex, newIndex)

      // Update order values to match new positions
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        order: index
      }))

      console.log('🔄 Reordered sections:', updatedSections.map(s => ({ id: s.id, title: s.title, order: s.order })))

      // Update local state immediately for instant feedback
      updateSections(updatedSections)

      // Save to backend
      try {
        const payload = updatedSections.map((s) => ({
          id: s.id,
          order: s.order
        }))

        await sdk.sections.reorderSections(payload)
        console.log('✅ Sections reordered successfully')
      } catch (error) {
        console.error('❌ Failed to reorder sections:', error)
        // Revert to original order on error
        const reorderedSections = [...sections].sort((a, b) => a.order - b.order)
        updateSections(reorderedSections)
      }
    }

    setActiveId(null)
    setActiveType(null)
    holdLive(false)
  }

  const handleDragCancel = () => {
    setActiveId(null)
    setActiveType(null)
    dragOriginSectionId.current = null
    holdLive(false)
    /* A space dragged into another section was already moved in the menu state: back to the server's. */
    refresh({ silent: true })
  }

  // Get the active section for the drag overlay
  const activeSection = activeId && activeType === 'section' ? sections.find(s => s.id === activeId) : null

  /* The dragged space, with the section it is currently in — the overlay needs
     both, and during a cross-section drag that section changes underneath it. */
  const activeSpace = React.useMemo(() => {
    if (!activeId || activeType !== 'space') return null
    for (const section of sections) {
      const space = (section.spaces || []).find(sp => sp.id === activeId)
      if (space) return { space, section }
    }
    return null
  }, [activeId, activeType, sections])

  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  }

  // Close sidebar when route changes on mobile
  React.useEffect(() => {
    close()
  }, [currentPath, close])

  /* The highlighted space follows the route, so bring it into the menu's
     view: a route opened from a link elsewhere (a post, Back) can land on a
     space far below the fold. A space that is already visible does not move. */
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const item = document.querySelector<HTMLElement>('[data-sidebar-scroll] [aria-current="page"]')
      const scroller = item?.closest<HTMLElement>('[data-sidebar-scroll]')
      if (!item || !scroller) return
      const itemBox = item.getBoundingClientRect()
      const box = scroller.getBoundingClientRect()
      if (itemBox.top < box.top) scroller.scrollTop -= box.top - itemBox.top + 8
      else if (itemBox.bottom > box.bottom) scroller.scrollTop += itemBox.bottom - box.bottom + 8
    }, 150)
    return () => window.clearTimeout(timer)
  }, [currentPath])

  // Filter sections based on authentication status
  const visibleSections = React.useMemo(() => {
    // API already filters sections based on authentication, just sort them
    /* Home first, then the API's order (a copy: sorting `sections` in place reorders the context's own array). */
    const filtered = [...sections].sort((a, b) => Number(!!b.isHome) - Number(!!a.isHome) || a.order - b.order)
    
    return filtered
  }, [sections])


  /* The aside's height uses `dvh`, not `vh`: on mobile Chrome `100vh` is the
     screen without the address bar, so while the bar shows the menu's bottom
     (Administration) sat under it and only appeared once the bar scrolled away.
     The bottom padding keeps the last item clear of the home indicator. */
  if (isLoading) {
    return (
      <aside className={cn(
        "theme-region-sidebar fixed top-16 left-0 h-[calc(100dvh-4rem)] bg-surface border-r border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-all duration-200 z-40 shadow-[8px_0_32px_rgba(0,0,0,0.04)]",
        "w-[--sidebar-width] lg:max-w-[--sidebar-width]",
        !isOpen && "max-lg:-translate-x-full",
        isCollapsed && "lg:-translate-x-full",
        /* F-191 phase 2: from 1024 px the rail takes its place. */
        mode === 'rail' && "lg:hidden",
        /* F-191 phase 5: the bar on top replaces it everywhere; it stays mounted for the counters it syncs. */
        isTopNav && "!hidden",
        "flex flex-col"
      )}>
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-8 bg-gray-200 rounded" />
          ))}
        </div>
      </aside>
    )
  }

  if (error) {
    return (
      <aside className={cn(
        "theme-region-sidebar fixed top-16 left-0 h-[calc(100dvh-4rem)] bg-surface border-r border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-all duration-200 z-40 shadow-[8px_0_32px_rgba(0,0,0,0.04)]",
        "w-[--sidebar-width] lg:max-w-[--sidebar-width]",
        !isOpen && "max-lg:-translate-x-full",
        isCollapsed && "lg:-translate-x-full",
        /* F-191 phase 2: from 1024 px the rail takes its place. */
        mode === 'rail' && "lg:hidden",
        /* F-191 phase 5: the bar on top replaces it everywhere; it stays mounted for the counters it syncs. */
        isTopNav && "!hidden",
        "flex flex-col"
      )}>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-sm text-red-500">{t('sidebar.errorLoadingMenu')}</p>
          <button
            type="button"
            onClick={() => { void refresh() }}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} aria-hidden="true" />
            {t('common:retry')}
          </button>
        </div>
      </aside>
    )
  }

  return (
    <aside className={cn(
      "theme-region-sidebar fixed top-16 left-0 h-[calc(100dvh-4rem)] bg-surface border-r border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-all duration-200 z-40 shadow-[8px_0_32px_rgba(0,0,0,0.04)]",
      "w-[--sidebar-width] lg:max-w-[--sidebar-width]",
      !isOpen && "max-lg:-translate-x-full",
      isCollapsed && "lg:-translate-x-full",
      mode === 'rail' && "lg:hidden",
        /* F-191 phase 5: the bar on top replaces it everywhere; it stays mounted for the counters it syncs. */
        isTopNav && "!hidden",
      "flex flex-col"
    )}>
      <ChannelUnreadSync spaceIds={channelSpaceIds} />
      <MenuCountersSync sections={sections} />
      {!isAuthenticated && (
        <div className="mb-3 pb-3 border-b border-gray-200 lg:hidden">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => {
                close()
                const loginButton = document.querySelector('[data-login-trigger]') as HTMLButtonElement
                if (loginButton) loginButton.click()
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              <User className="h-4 w-4" />
              {t('signIn')}
            </button>
            {community?.signupEnabled && (
              <button
                onClick={() => {
                  close()
                  const signupButton = document.querySelector('[data-signup-trigger]') as HTMLButtonElement
                  if (signupButton) signupButton.click()
                }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors bg-primary text-white hover:bg-primary-600"
              >
                {community.signupRequiresApproval ? t('auth:requestAccess') : t('signUp')}
              </button>
            )}
          </div>
        </div>
      )}
      {/* The scrollbar itself is the cue. It is **always** visible instead of
          only while scrolling (the macOS default, and the reason nobody could
          tell the menu went on). No gradient: it covered the last item and
          looked like dirt on the edge. */}
      <nav
        data-sidebar-scroll
        className={cn(
          'min-h-0 flex-1 space-y-2 overflow-y-auto pr-1.5',
          '[scrollbar-color:theme(colors.gray.300)_transparent] [scrollbar-width:thin]',
          '[&::-webkit-scrollbar]:w-1.5',
          '[&::-webkit-scrollbar-track]:bg-transparent',
          '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300',
          'hover:[&::-webkit-scrollbar-thumb]:bg-gray-400'
        )}
      >
        {visibleSections.length > 0 ? (
          <>
            {/* One context for the whole sidebar. It used to be one per section,
                which is why a space could never leave the section it was in:
                dnd-kit only reports a drop inside the context the drag began in,
                so dragging out simply produced no `over` and nothing happened. */}
            <DndContext
              sensors={sensors}
              collisionDetection={sidebarCollisionDetection}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={visibleSections.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {visibleSections.map(section => {
                  return (
                    <SidebarSection
                      key={section.id}
                      section={section}
                      isAuthenticated={isAuthenticated}
                      currentPath={currentPath}
                      sections={sections}
                      canEdit={canEdit}
                      onRefresh={refresh}
                    />
                  )
                })}
              </SortableContext>
              <DragOverlay dropAnimation={dropAnimationConfig} modifiers={[adjustTopModifier]}>
                {activeSection ? (
                  <div className="w-[calc(var(--sidebar-width)-1.5rem)]">
                    <div className="space-y-1">
                      <div className="bg-white rounded-lg shadow-xl border border-gray-200 cursor-grabbing">
                        <div className="flex items-center justify-between w-full px-3 py-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            {(() => {
                              const IconComponent = getIconComponent(activeSection.icon || 'folder')
                              return <IconComponent className="h-4 w-4 flex-shrink-0" />
                            })()}
                            <span className="truncate">{activeSection.title}</span>
                          </div>
                          {activeSection.spaces && activeSection.spaces.length > 0 && (
                            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                      {activeSection.spaces && activeSection.spaces.length > 0 && (
                        <div className="ml-4 space-y-1">
                          {activeSection.spaces.slice(0, 3).map(space => {
                            const SpaceIcon = getIconComponent(space.icon || 'hash')
                            return (
                              <div
                                key={space.id}
                                className="bg-white rounded-lg px-3 py-1.5 text-sm text-gray-700 flex items-center gap-2 opacity-60"
                              >
                                <SpaceIcon className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{space.title}</span>
                              </div>
                            )
                          })}
                          {activeSection.spaces.length > 3 && (
                            <div className="px-3 py-1 text-xs text-gray-400">
                              +{activeSection.spaces.length - 3} mais
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
                {activeSpace ? (
                  <div className="w-[calc(var(--sidebar-width)-1.5rem)]">
                    <SpaceContent
                      space={activeSpace.space}
                      section={activeSpace.section}
                      currentPath={currentPath}
                      defaultHomeSpace={null}
                      canEdit={false}
                      isActive={false}
                      isDragOverlay={true}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
            {canEdit && (
              <button
                onClick={handleAddSection}
                className="flex items-center justify-center gap-2 w-full px-3 py-1.5 mt-2 text-sm text-gray-400 border border-gray-200 rounded-lg transition-colors hover:bg-gray-50 hover:text-gray-600 hover:border-gray-300"
                aria-label={t('sidebar.addSection')}
              >
                <Plus className="h-4 w-4" />
                <span>{t('sidebar.addSection')}</span>
              </button>
            )}

            <CreateSectionDialog
              open={createDialogOpen}
              onOpenChange={setCreateDialogOpen}
              onSectionCreated={refresh}
            />
          </>
        ) : (
          <div className="space-y-2">
            {canEdit && (
              <>
                <button
                  onClick={handleAddSection}
                  className="flex items-center justify-center gap-2 w-full px-3 py-1.5 text-sm text-gray-400 border border-gray-200 rounded-lg transition-colors hover:bg-gray-50 hover:text-gray-600 hover:border-gray-300"
                  aria-label={t('sidebar.addSection')}
                >
                  <Plus className="h-4 w-4" />
                  <span>{t('sidebar.addSection')}</span>
                </button>
                <CreateSectionDialog
                  open={createDialogOpen}
                  onOpenChange={setCreateDialogOpen}
                  onSectionCreated={refresh}
                />
              </>
            )}
          </div>
        )}
      </nav>

      {/* F-184: My account, Sign out and the personal group (Reportar problema, Faturamento, Minha empresa,
          Eventos que organizo, Engajamento geral) live in the header avatar's panel now (David, 2026-10-29). */}
      {/* Meus ingressos, Minhas compras and Créditos (David, 2026-11-05): pinned above Administração, outside the scroll. */}
      <SidebarPersonalLinks />

      {/* `min-h-0 overflow-y-auto`: on a short screen with the accordion open, the
          Administration list scrolls inside the drawer instead of spilling below it. */}
      {isAuthenticated && canSeeAdmin && (
        <div className="min-h-0 overflow-y-auto border-t border-gray-200 pt-3 pb-2">
            <div className="space-y-1">
              <button
                type="button"
                onClick={toggleAdmin}
                aria-expanded={adminOpen}
                aria-controls="sidebar-admin-items"
                className="flex w-full items-center justify-between px-3 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-wider rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span>{t('administration')}</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", !adminOpen && "-rotate-90")} aria-hidden="true" />
              </button>
              <div id="sidebar-admin-items" hidden={!adminOpen} className="space-y-1">
              {adminItems.filter((item) => item.visible).map((item) => (
              <AppLink
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors w-full",
                  isRouteActive(currentPath, item.path)
                    ? "bg-primary-50 backdrop-blur-sm text-primary shadow-[0_4px_12px_rgba(255,101,0,0.12)]"
                    : "text-gray-600 hover:bg-gray-50"
                )}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </AppLink>
              ))}
              </div>
            </div>
        </div>
      )}

      {/* F-201: reopens the cookie notice; only where there is a choice to make (the community's own GA). */}
      {hasCookieChoice && (
        <div className="mt-auto pt-3 text-center">
          <button type="button" onClick={openCookieNotice} className="text-xs text-gray-400 transition-colors hover:text-gray-600" data-testid="sidebar-cookies">
            {t('common:cookies.link')}
          </button>
        </div>
      )}

      {/* F-34: the plan keeps the "Powered by Memberfy" mark (Starter); the API decides. */}
      {community?.branding?.showMemberfyMark === true && (
        <div className={cn('pt-3 text-center', !hasCookieChoice && 'mt-auto')} data-testid="memberfy-mark">
          <a
            href="https://memberfy.net"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-400 transition-colors hover:text-gray-600"
          >
            {t('entitlements:poweredBy')}
          </a>
        </div>
      )}
    </aside>
  )
}